import * as React from 'react';
import moment from 'moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { makeStyles } from '@material-ui/core/styles';
import { useFleetInputStyles } from './inputStyles';

/** MUI v4 date picker (equivalent of ClientApp2 CustomDatePicker) — value/onChange in `YYYY-MM-DD`. */
export interface CustomDatePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: React.ReactNode;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  format?: string;
  disabled?: boolean;
  disablePastDates?: boolean;
  minDate?: string | null;
  maxDate?: string | null;
  error?: boolean;
}

const WIRE_FORMAT = 'YYYY-MM-DD';

// idem ClientApp2 CustomDatePicker: 14px value text + 18px calendar icon pinned to the far right.
const useLocalStyles = makeStyles({
  icon: {
    '& [class*="MuiSvgIcon-root"]': { width: 18, height: 18 },
    '& [class*="MuiIconButton-root"]': { padding: 4 },
    '& [class*="MuiOutlinedInput-adornedEnd"]': { paddingRight: 4 }
  }
});

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder,
  size = 'medium',
  fullWidth = true,
  format = 'DD/MM/YYYY',
  disabled,
  disablePastDates = false,
  minDate,
  maxDate,
  error
}) => {
  const classes = useFleetInputStyles();
  const local = useLocalStyles();
  const [open, setOpen] = React.useState(false);
  // idem ClientApp2 CustomDatePicker: bounded range + disablePast when past dates aren't allowed.
  const minMoment = minDate ? moment(minDate, WIRE_FORMAT) : moment('2005-01-01');
  const maxMoment = maxDate ? moment(maxDate, WIRE_FORMAT) : moment().add(10, 'year');
  return (
    <MuiPickersUtilsProvider utils={MomentUtils} libInstance={moment}>
      <KeyboardDatePicker
        className={`${classes.root} ${local.icon}${error ? ' ' + classes.error : ''}`}
        inputVariant="outlined"
        variant="inline"
        size={size}
        fullWidth={fullWidth}
        autoOk
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        InputAdornmentProps={{ position: 'end' }}
        InputProps={{ readOnly: true, onClick: disabled ? undefined : () => setOpen(true) }}
        InputLabelProps={{ shrink: true }}
        format={format}
        placeholder={placeholder || format}
        label={label}
        disabled={disabled}
        disablePast={disablePastDates}
        minDate={minMoment}
        maxDate={maxMoment}
        shouldDisableDate={(date) =>
          disablePastDates && date != null && date.isBefore(moment(), 'day')
        }
        value={value ? moment(value, WIRE_FORMAT) : null}
        onChange={(date) => onChange(date && date.isValid() ? date.format(WIRE_FORMAT) : null)}
      />
    </MuiPickersUtilsProvider>
  );
};

export default CustomDatePicker;
